import { useId } from 'react';
import { MIN_PASSWORD_LENGTH } from '@palacete/shared/password-policy';
import PasswordStrengthMeter from './PasswordStrengthMeter';

interface PasswordFieldProps {
  id?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  // 'create' = se está eligiendo una contraseña nueva (exige mínimo, muestra fuerza).
  // 'current' = se está verificando una contraseña existente para entrar (login, reautenticación,
  // "contraseña actual" antes de cambiarla) — no aplica el mínimo de hoy a contraseñas antiguas.
  variant?: 'create' | 'current';
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  autoComplete?: string;
  className?: string;
  // El input contrasta contra su contenedor inmediato: 'bg' (default) cuando el contenedor es
  // bg-ui-surface, 'surface' cuando el input vive anidado dentro de un bloque ya bg-ui-bg.
  surface?: 'bg' | 'surface';
}

export default function PasswordField({
  id,
  label,
  value,
  onChange,
  variant = 'create',
  required = true,
  autoFocus,
  placeholder,
  autoComplete,
  className,
  surface = 'bg',
}: PasswordFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const strengthId = `${inputId}-strength`;
  const isCreate = variant === 'create';

  return (
    <div className={className}>
      <label htmlFor={inputId} className="block text-sm text-ui-text-muted mb-1">{label}</label>
      <input
        id={inputId}
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        autoFocus={autoFocus}
        minLength={isCreate ? MIN_PASSWORD_LENGTH : undefined}
        autoComplete={autoComplete ?? (isCreate ? 'new-password' : 'current-password')}
        aria-describedby={isCreate ? `${hintId} ${strengthId}` : undefined}
        placeholder={placeholder ?? (isCreate ? `Mínimo ${MIN_PASSWORD_LENGTH} caracteres` : '••••••••')}
        className={`w-full ${surface === 'surface' ? 'bg-ui-surface' : 'bg-ui-bg'} border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent`}
      />
      {isCreate && (
        <>
          <p id={hintId} className="sr-only">
            Mínimo {MIN_PASSWORD_LENGTH} caracteres. La fuerza de la contraseña se muestra mientras escribes.
          </p>
          <PasswordStrengthMeter password={value} id={strengthId} />
        </>
      )}
    </div>
  );
}
